/**
 * Utilitaire pour charger et vérifier les variables d'environnement
 * Permet de centraliser la logique de récupération des variables d'environnement
 * et d'ajouter des valeurs par défaut ou des fallbacks
 */

// Style public Mapbox qui fonctionne toujours
export const PUBLIC_MAPBOX_STYLE = 'mapbox://styles/mapbox/streets-v11';
// Token de démonstration public (limité) pour le développement uniquement
export const PUBLIC_DEMO_TOKEN = 'pk.eyJ1IjoiZXhhbXBsZXMiLCJhIjoiY2p0MG01MXRqMW45cjQzb2R6b2ptc3J4MSJ9.zA2W0IkI0c6KaAhJfk9bWg';

/**
 * Récupère une variable d'environnement avec fallback
 * @param name Nom de la variable d'environnement
 * @param defaultValue Valeur par défaut si la variable n'est pas définie
 * @returns La valeur de la variable d'environnement ou la valeur par défaut
 */
export const getEnvVariable = (name: string, defaultValue: string = ''): string => {
  // Vérifier d'abord dans process.env (variables injectées au build)
  const processValue = process.env[name];
  if (processValue) {
    console.log(`✅ Variable ${name} trouvée dans process.env`);
    return processValue;
  }
  
  // Vérifier ensuite dans window (variables injectées dynamiquement)
  const windowValue = (window as any)[name];
  if (windowValue) {
    console.log(`✅ Variable ${name} trouvée dans window`);
    return windowValue;
  }
  
  // Utiliser la valeur par défaut si aucune variable n'est trouvée
  console.warn(`⚠️ Variable ${name} non trouvée, utilisation de la valeur par défaut`);
  return defaultValue;
};

/**
 * Récupère le token Mapbox avec fallback sur le token public
 */
export const getMapboxToken = (): string => {
  return getEnvVariable('REACT_APP_MAPBOX_TOKEN', PUBLIC_DEMO_TOKEN);
};

/**
 * Récupère le style Mapbox avec fallback sur le style public
 */
export const getMapboxStyle = (): string => {
  return getEnvVariable('REACT_APP_MAPBOX_STYLE', PUBLIC_MAPBOX_STYLE);
};

/**
 * Vérifie si les variables d'environnement Mapbox sont correctement configurées
 * @returns Un objet contenant les variables et un statut de configuration
 */
export const checkMapboxConfig = () => {
  const token = getMapboxToken();
  const style = getMapboxStyle();
  
  const isUsingPublicToken = token === PUBLIC_DEMO_TOKEN;
  const isUsingPublicStyle = style === PUBLIC_MAPBOX_STYLE;
  
  return {
    token,
    style,
    isConfigured: !isUsingPublicToken,
    isUsingPublicToken,
    isUsingPublicStyle
  };
};

